import { CardTypeMap } from '../../Prototype/Level/Card/CardData';
import { CardModifierType } from '../../Prototype/Level/Card/CardModifierType';
import { CardStatus } from '../../Prototype/Level/Card/CardStatus';
import { CardType } from '../../Prototype/Level/Card/CardType';
import {
    DEFAULT_CARD_HEIGHT,
    DEFAULT_CARD_WIDTH,
} from '../../Prototype/Level/Card/ICardData';
import CardsHelper from '../../Utility/CardsHelper';
import { Random } from '../../Utility/Random';
import {
    TActionType,
    TransactionType,
} from '../../Utility/Tracking/DataSet/Transaction';
import { track, TrackMethod } from '../../Utility/Tracking/Track';
import { trackTransaction } from '../../Utility/Tracking/TrackingHelpers/Transaction';
import { IGameState } from '../Game/IGameState';
import BaseBoosterBehavior from './BoosterBehavior/BaseBoosterBehavior';
import { BoosterType, BoosterTypeMap } from './BoosterType';
import BaseCardBehavior from './CardBehavior/BaseCardBehavior';
import { ICardsState, ICardStateData, ICardStateTaskData } from './CardReducer';
import CardStateModifierHelper from './CardStateModifierHelper';
import { CardTask } from './CardTasks';

export default class CardStateHelper {
    public static addCard(
        state: IGameState,
        cardData: ICardStateData
    ): IGameState {
        return {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState: {
                    ...state.levelState.cardsState,
                    cards: {
                        ...state.levelState.cardsState.cards,
                        [cardData.id]: cardData,
                    },
                },
            },
        };
    }

    public static playBooster(
        state: IGameState,
        type: BoosterType
    ): IGameState {
        const behavior = CardStateHelper.getBoosterBehavior(state, type);

        if (behavior.isPlayable(state)) {
            state = behavior.play(state);

            // Handle card state after booster is played.
            state = CardStateHelper.updateCards(state);

            // Check if cards need to be flipped.
            state = CardStateHelper.checkCardsObscured(state);

            // Check if next card should be folded.
            state = CardStateHelper.checkFoldNextCard(state);

            // Update cards after a booster is played.
        }

        return state;
    }

    public static playCard(state: IGameState, cardID: string): IGameState {
        if (!state.levelState.cardsState.cards[cardID]) return state;

        const behavior = CardStateHelper.getCardBehavior(state, cardID);

        const currentCard = CardStateHelper.getCurrentCard(state);
        if (behavior.isPlayable(state, currentCard)) {
            state = behavior.play(state);

            // Increment the number of completed 'turns'.
            state = {
                ...state,
                levelState: {
                    ...state.levelState,
                    cardsState: {
                        ...state.levelState.cardsState,
                        turns: state.levelState.cardsState.turns + 1,
                    },
                },
            };

            // Check if cards need to be flipped.
            state = CardStateHelper.checkCardsObscured(state);

            // Check if next card should be folded.
            state = CardStateHelper.checkFoldNextCard(state);
        } else {
            if (
                CardStateHelper.getCardStatus(state, cardID) ===
                CardStatus.IN_PLAY
            ) {
                state.levelState.cardsState.cards[cardID].attempts += 1;
                state = CardStateHelper.setStateTaskData(state, {
                    task: CardTask.WIGGLE,
                    cards: [cardID],
                });
            }
        }
        return state;
    }

    public static setCardInPlay(state: IGameState, cardID: string): IGameState {
        state = CardStateHelper.removeFromStateArrays(state, cardID);

        return {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState: {
                    ...state.levelState.cardsState,
                    inPlay: [...state.levelState.cardsState.inPlay, cardID],
                },
            },
        };
    }

    public static setCardInStack(
        state: IGameState,
        cardID: string
    ): IGameState {
        state = CardStateHelper.removeFromStateArrays(state, cardID);

        const cards = { ...state.levelState.cardsState.cards };

        state = setCardState(state, {
            ...cards[cardID],
            faceUp: false,
            x: -150,
            y: 0,
            rotation: 0,
        });

        return {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState: {
                    ...state.levelState.cardsState,
                    cards,
                    inStack: [...state.levelState.cardsState.inStack, cardID],
                },
            },
        };
    }

    public static setCardFolded(state: IGameState, cardID: string): IGameState {
        state = CardStateHelper.removeFromStateArrays(state, cardID);

        state = setCardState(state, {
            ...state.levelState.cardsState.cards[cardID],
            faceUp: true,
            x: 0,
            y: 0,
            rotation: 0,
        });

        return {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState: {
                    ...state.levelState.cardsState,
                    folded: [...state.levelState.cardsState.folded, cardID],
                },
            },
        };
    }

    public static setCardDiscarded(
        state: IGameState,
        cardID: string
    ): IGameState {
        state = CardStateHelper.removeFromStateArrays(state, cardID);

        return {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState: {
                    ...state.levelState.cardsState,
                    discarded: [
                        ...state.levelState.cardsState.discarded,
                        cardID,
                    ],
                },
            },
        };
    }

    /**
     * Removes specified card ID from all arrays - overwrites all arrays.
     * @param state
     * @param cardID
     */
    public static removeFromStateArrays(
        state: IGameState,
        cardID: string
    ): IGameState {
        const cardsState = {
            ...state.levelState.cardsState,
        };

        cardsState.inPlay = cardsState.inPlay.filter(value => value !== cardID);
        cardsState.inStack = cardsState.inStack.filter(
            value => value !== cardID
        );
        cardsState.folded = cardsState.folded.filter(value => value !== cardID);
        cardsState.discarded = cardsState.discarded.filter(
            value => value !== cardID
        );

        return {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState,
            },
        };
    }

    public static checkCardsObscured(state: IGameState): IGameState {
        const flippedCards: string[] = [];

        state.levelState.cardsState.inPlay.forEach(cardID => {
            const card = state.levelState.cardsState.cards[cardID];
            const behavior = CardStateHelper.getCardBehavior(state, cardID);

            // Set faceUp to true if the card was previously face-down and is no longer obscured.
            if (card.faceUp === false && !behavior.isObscured(state)) {
                state = setCardState(state, {
                    ...card,
                    faceUp: true,
                });

                // Collect cards for flipping animations.
                flippedCards.push(cardID);
            }
        });

        if (flippedCards.length > 0) {
            state = CardStateHelper.setStateTaskData(state, {
                task: CardTask.FLIP,
                cards: flippedCards,
            });
        }

        return state;
    }

    public static checkFoldNextCard(state: IGameState): IGameState {
        if (
            state.levelState.cardsState.inStack.length > 0 &&
            state.levelState.cardsState.folded.length === 0
        ) {
            const cardID =
                state.levelState.cardsState.inStack[
                    state.levelState.cardsState.inStack.length - 1
                ];
            state = CardStateHelper.setCardFolded(state, cardID);
            state = CardStateHelper.setStateTaskData(state, {
                task: CardTask.FOLD,
                cards: [cardID],
            });
        }

        return state;
    }

    public static getCardStatus(state: IGameState, cardID: string): CardStatus {
        if (state.levelState.cardsState.inPlay.indexOf(cardID) !== -1) {
            return CardStatus.IN_PLAY;
        }
        if (state.levelState.cardsState.inStack.indexOf(cardID) !== -1) {
            return CardStatus.IN_STACK;
        }
        if (state.levelState.cardsState.folded.indexOf(cardID) !== -1) {
            return CardStatus.FOLDED;
        }
        if (state.levelState.cardsState.discarded.indexOf(cardID) !== -1) {
            return CardStatus.DISCARDED;
        }
        return CardStatus.UNKNOWN;
    }

    public static getCardBehavior(
        state: IGameState,
        cardID: string
    ): BaseCardBehavior {
        return new CardTypeMap[
            state.levelState.cardsState.cards[cardID].type
        ].behavior(cardID);
    }

    public static getBoosterBehavior(
        state: IGameState,
        type: BoosterType
    ): BaseBoosterBehavior {
        return new BoosterTypeMap[type].behavior();
    }

    public static getCurrentCard(state: IGameState): string {
        return state.levelState.cardsState.folded[
            state.levelState.cardsState.folded.length - 1
        ];
    }

    /**
     * Update all cards (called after a 'valid' turn).
     * @param state
     */
    public static updateCards(state: IGameState): IGameState {
        Object.keys(state.levelState.cardsState.cards).forEach(
            cardID =>
                (state = CardStateHelper.getCardBehavior(state, cardID).update(
                    state
                ))
        );
        return state;
    }

    public static setStateTaskData(
        state: IGameState,
        taskData: ICardStateTaskData
    ): IGameState {
        return {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState: {
                    ...state.levelState.cardsState,
                    tasks: [...state.levelState.cardsState.tasks, taskData],
                },
            },
        };
    }

    public static getCardMatches(state: IGameState): string[] {
        return state.levelState.cardsState.inPlay.filter(inPlayCardID => {
            const behavior = CardStateHelper.getCardBehavior(
                state,
                inPlayCardID
            );
            return behavior.isPlayable(
                state,
                CardStateHelper.getCurrentCard(state)
            );
        });
    }

    public static plusStreakCards(state: IGameState): IGameState {
        if (!state.levelState.cardsState.streakEnabled) {
            return state;
        }

        const targets: string[] = [];

        state = this.addNewCards(
            state,
            state.levelState.cardsState.streakCards,
            targets
        );
        track(
            'mExtraCardsWon',
            state.levelState.cardsState.streakCards,
            TrackMethod.ADD
        );
        track(
            'tLevelExtraCardsReceived',
            state.levelState.cardsState.streakCards,
            TrackMethod.ADD
        );
        track(
            'tStreakExtraCards',
            state.levelState.cardsState.streakCards,
            TrackMethod.ADD
        );
        trackTransaction(state, TransactionType.REWARD, TActionType.REWARD);

        // All other bonus streaks inside the same level should give +2 cards (even if player resets the card streak).
        if (state.levelState.cardsState.streakSequence >= 5) {
            state = {
                ...state,
                levelState: {
                    ...state.levelState,
                    cardsState: {
                        ...state.levelState.cardsState,
                        streakCards: 2,
                    },
                },
            };
        }

        // Trigger plus streak cards animation task.
        state = CardStateHelper.setStateTaskData(state, {
            task: CardTask.PLUS_STREAK,
            cards: targets,
        });

        return state;
    }

    public static addNewCards(
        state: IGameState,
        amount: number,
        cardIDs: string[]
    ): IGameState {
        // Keep track of card values drawn to prevent duplicate values.
        const drawnValues: number[] = [];
        for (let i = 0; i < amount; i++) {
            const newCardID = `card_${Random.ID()}`;

            // Draw a new card.
            let value = CardsHelper.instance.draw();

            // If the un-suited value of the card was already drawn keep trying.
            while (drawnValues.indexOf(value % 13) !== -1) {
                value = CardsHelper.instance.draw();
            }

            // Store the un-suited value of the card.
            drawnValues.push(value % 13);

            state = CardStateHelper.addCard(state, {
                id: newCardID,
                type: CardType.value,
                faceUp: false,
                depth: 0,
                rotation: 0,
                modifiers: [],
                sequence: 0,
                attempts: 0,
                value: value,
                random: true,
                x: 0,
                y: 0,
                width: DEFAULT_CARD_WIDTH,
                height: DEFAULT_CARD_HEIGHT,
            });
            state = CardStateHelper.setCardInStack(state, newCardID);
            cardIDs.push(newCardID);
        }

        return state;
    }

    public static increaseStreak(
        state: IGameState,
        cardScore: number
    ): IGameState {
        track(
            'mHighestPlayStreak',
            state.levelState.cardsState.streakSequence + 1,
            TrackMethod.MAX
        );
        return {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState: {
                    ...state.levelState.cardsState,
                    streakSequence:
                        state.levelState.cardsState.streakSequence + 1,
                    streakScore:
                        state.levelState.cardsState.streakScore + cardScore,
                },
            },
        };
    }

    public static cancelStreak(state: IGameState): IGameState {
        return {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState: {
                    ...state.levelState.cardsState,
                    streakSequence: 0,
                    streakScore: 0,
                },
            },
        };
    }

    public static cancelStreakScore(state: IGameState): IGameState {
        return {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState: {
                    ...state.levelState.cardsState,
                    streakScore: 0,
                },
            },
        };
    }

    public static addTurnScore(
        state: IGameState,
        ...score: number[]
    ): IGameState {
        return {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState: {
                    ...state.levelState.cardsState,
                    turnScore:
                        state.levelState.cardsState.turnScore +
                        score.reduce((a, c) => a + c, 0),
                },
            },
        };
    }

    public static pushUndoHistory(
        state: IGameState,
        cardID: string,
        cardState: CardStatus
    ): IGameState {
        // Ignore first turn.
        if (state.levelState.cardsState.turns === 0) return state;

        // Modifier array is emptied. Modifiers are not restored after an undo booster is played.
        return {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState: {
                    ...state.levelState.cardsState,
                    undoHistory: [
                        ...state.levelState.cardsState.undoHistory,
                        {
                            ...state.levelState.cardsState.cards[cardID],
                            modifiers: [],
                            cardState: cardState,
                        },
                    ],
                },
            },
        };
    }

    public static clearUndoHistory(state: IGameState): IGameState {
        return {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState: {
                    ...state.levelState.cardsState,
                    undoHistory: [],
                },
            },
        };
    }

    public static checkStreakBonus(state: IGameState): IGameState {
        // 1st bonus streak rewards +1 card.
        // 2nd, 3rd ... bonus streaks reward +2 cards.
        if (
            state.levelState.cardsState.streakSequence > 0 &&
            state.levelState.cardsState.streakSequence % 5 === 0
        ) {
            state = CardStateHelper.plusStreakCards(state);
        }
        return state;
    }

    /**
     * Face-up and face-down cards are shuffled separately and concatenated.
     * This is done to prioritize replacing face-up cards with face-down cards when shuffling all value cards.
     * @param state
     */
    public static shuffleValueCards(state: IGameState): IGameState {
        const faceUpCardIDs: string[] = [];
        const faceDownCardIDs: string[] = [];

        CardStateHelper.getValueCards(state).forEach(cardID => {
            const behavior = CardStateHelper.getCardBehavior(state, cardID);
            if (behavior.isFaceUp(state)) {
                faceUpCardIDs.push(cardID);
            } else {
                faceDownCardIDs.push(cardID);
            }
        });

        // Note the order of the following shuffled arrays: face-up and face-down are reversed.
        const shuffledCardIDs = [
            ...Random.shuffle(faceUpCardIDs, true),
            ...Random.shuffle(faceDownCardIDs, true),
        ];
        const shuffledTargetCardIDs = [
            ...Random.shuffle(faceDownCardIDs, true),
            ...Random.shuffle(faceUpCardIDs, true),
        ];

        const shuffledCards = { ...state.levelState.cardsState.cards };

        shuffledCardIDs.forEach((cardID: string, index: number) => {
            shuffledCards[cardID] = {
                ...shuffledCards[cardID],
                value:
                    state.levelState.cardsState.cards[
                        shuffledTargetCardIDs[index]
                    ].value,
            };
        });

        return {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState: {
                    ...state.levelState.cardsState,
                    cards: shuffledCards,
                },
            },
        };
    }

    /**
     * Get all value cards in-play unless blocked by fog.
     * @param state
     */
    public static getValueCards(state: IGameState): string[] {
        return state.levelState.cardsState.inPlay.filter(cardID => {
            if (
                state.levelState.cardsState.cards[cardID].type !==
                CardType.value
            )
                return false;

            const modifier = state.levelState.cardsState.cards[
                cardID
            ].modifiers.find(
                modifierData => modifierData.type === CardModifierType.fog
            );
            if (modifier) {
                const behavior = CardStateModifierHelper.getModifierBehavior(
                    state,
                    modifier,
                    cardID
                );
                return !behavior.isBlocking(state);
            }

            return true;
        });
    }
}

export function getAllCards(state: ICardsState): ICardStateData[] {
    return [
        ...state.folded,
        ...state.discarded,
        ...state.inPlay,
        ...state.inStack,
    ].map(cardID => state.cards[cardID]);
}

export function getGoldCardsTotal(state: ICardsState): number {
    return getAllCards(state).filter(card => card.type === CardType.gold)
        .length;
}

export function getGoldCardsInPlay(state: ICardsState): number {
    return [...state.inPlay].filter(
        cardID => state.cards[cardID].type === CardType.gold
    ).length;
}

export function getGoldCardsCollected(state: ICardsState): number {
    return [...state.folded, ...state.discarded].filter(
        cardID => state.cards[cardID].type === CardType.gold
    ).length;
}

export function setCardState(
    state: IGameState,
    card: ICardStateData
): IGameState {
    return {
        ...state,
        levelState: {
            ...state.levelState,
            cardsState: {
                ...state.levelState.cardsState,
                cards: {
                    ...state.levelState.cardsState.cards,
                    [card.id]: card,
                },
            },
        },
    };
}

export function setNewTurnCardState(state: IGameState): IGameState {
    return {
        ...state,
        levelState: {
            ...state.levelState,
            cardsState: {
                ...state.levelState.cardsState,
                cards: { ...state.levelState.cardsState.cards },
                tasks: [] /* Tasks are cleared each turn. */,
                turnScore: 0,
            },
        },
    };
}
